import { flip, type OffsetOptions, offset, shift, size } from '@floating-ui/react';
import * as Sentry from '@sentry/react';
import clsx from 'clsx';
import * as React from 'react';

import type { PopoverProps } from '../Popover';
import { MenuPlaceholder } from './MenuPlaceholder';

export {
    OverlayTrigger as MenuTrigger,
    useOverlayClose as useMenuClose,
    useOverlayTriggerRef as useMenuTriggerRef,
    useOverlayTriggerState as useMenuTriggerState,
} from '../Overlay';

/**
 * Get the middleware used for Floating UI in a menu.
 */
export function getMenuFloatingMiddleware(props: { offset: OffsetOptions }) {
    return [
        offset(props.offset),
        shift({ padding: 16 }),
        size({
            padding: 16,
            apply: ({ availableHeight, elements }) => {
                const value = `${Math.max(0, availableHeight)}px`;
                elements.floating.style.setProperty('--available-height', value);
            },
        }),
    ];
}

/**
 * Props used to configure a menu.
 */
export const menuPopoverProps = {
    placement: 'bottom' as const,
    floating: {
        middleware: [
            ...getMenuFloatingMiddleware({ offset: 8 }),
            flip({
                padding: 8,
                crossAxis: false,
            }),
        ],
    },
    className: clsx(
        'flex max-h-[min(max(var(--available-height,0),15rem),27rem)] flex-col has-[[data-spine-menu][data-ready=false]]:invisible'
    ),
    autoFocus: false,
    wrap: (children) => (
        <Sentry.ErrorBoundary
            fallback={<MenuPlaceholder>Error while loading actions</MenuPlaceholder>}
        >
            <React.Suspense fallback={<MenuPlaceholder>Loading…</MenuPlaceholder>}>
                {children}
            </React.Suspense>
        </Sentry.ErrorBoundary>
    ),
} satisfies Partial<PopoverProps>;
